import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Switch from '../../components/UI/Switch';
import toast from 'react-hot-toast';
import ErrorMessage from '../../components/UI/ErrorMessage';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const user = await login(data);
      const target = user.role === 'agent' ? '/agent-dashboard' : from;
      navigate(target, { replace: true });
    } catch (error) {
      // Error is handled in the AuthContext
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent/20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl border border-border p-8 space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                <PhoneIcon className="h-8 w-8 text-primary-foreground" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-foreground">
              Welcome back
            </h2>
            <p className="mt-3 text-muted-foreground">
              Sign in to your account to continue
            </p>
            {/* Signup disabled: hide self-service registration link */}
            <p className="mt-4 text-sm text-muted-foreground">
              Don't have an account? Contact your administrator.
            </p>
            {/* <p className="mt-4 text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Button
                to="/register"
                className="font-semibold text-primary hover:text-primary/80 transition-colors duration-200 p-0 h-auto"
                variant="ghost"
              >
                Create one here
              </Button>
            </p> */}
          </div>
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email address
              </label>
              <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="input"
                  placeholder="Enter your email"
                />
              <ErrorMessage message={errors.email?.message} />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="input pr-12"
                  placeholder="Enter your password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors duration-200 h-auto p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </Button>
              </div>
              <ErrorMessage message={errors.password?.message} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Switch
                {...register('rememberMe')}
                label="Remember me"
              />
            </div>

            <div className="text-sm">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                to="/forgot-password"
                className="font-medium text-primary hover:text-primary/80 p-0 h-auto"
              >
                Forgot your password?
              </Button>
            </div>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={isSubmitting || loading}
            >
              Sign in
            </Button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;